import { useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet, X } from "lucide-react";
import { toast } from "sonner";
import type {
  BulkImportEmployeeRow,
  BulkImportEmployeesResult,
  EmployeeStatus,
} from "@/lib/employees-api";
import { importFailureMessage } from "@/lib/import-error";

const TEMPLATE_URL = `${import.meta.env.BASE_URL}employee-import-template.xlsx`;
const TEMPLATE_HEADER = "empCode,name,email,phone,department,role,primarySiteCode,managerCode,status";
const CSV_PLACEHOLDER = [
  TEMPLATE_HEADER,
  "GK001,Aisyah Aziz,aisyah@example.com,+60111111111,Operations,Field Officer,KL-MAIN,GK100,ACTIVE",
  "GK002,Ahmad Faiz,ahmad@example.com,,Maintenance,Technician,,GK100,ACTIVE",
  "GK100,Nurul Izzah,nurul@example.com,,Operations,Supervisor,KL-MAIN,,ACTIVE",
].join("\n");

const VALID_STATUS: EmployeeStatus[] = ["ACTIVE", "INACTIVE"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type ParsedRow = BulkImportEmployeeRow & { _lineNumber: number; _error?: string };

const cell = (v: unknown) => String(v ?? "").trim();
const key = (v: unknown) => cell(v).toLowerCase();
const isExample = (empCode: string) => empCode.trim().toLowerCase().startsWith("example");

/** Build one validated row from a header-keyed accessor. Shared by CSV + xlsx. */
function buildRow(get: (headerKey: string) => string, lineNumber: number): ParsedRow {
  const statusRaw = get("status").toUpperCase();
  const row: ParsedRow = {
    _lineNumber: lineNumber,
    empCode: get("empcode"),
    name: get("name"),
    email: get("email") || undefined,
    phone: get("phone") || undefined,
    department: get("department") || undefined,
    role: get("role") || undefined,
    primarySiteCode: get("primarysitecode") || undefined,
    managerCode: get("managercode") || undefined,
    status: statusRaw ? (statusRaw as EmployeeStatus) : undefined,
  };
  if (!row.empCode) row._error = "Missing empCode";
  else if (!row.name) row._error = "Missing name";
  else if (row.email && !EMAIL_RE.test(row.email)) row._error = "Invalid email";
  else if (row.status && !VALID_STATUS.includes(row.status)) row._error = `Invalid status '${row.status}'`;
  return row;
}

function parseCsv(text: string): ParsedRow[] {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length < 2) return [];
  const header = lines[0].split(",").map(key);
  return lines
    .slice(1)
    .map((line, i) => {
      const cols = line.split(",").map((s) => s.trim());
      const get = (headerKey: string) => {
        const idx = header.indexOf(headerKey);
        return idx >= 0 ? cell(cols[idx]) : "";
      };
      return buildRow(get, i + 2);
    })
    .filter((r) => !isExample(r.empCode));
}

/** Parse the filled-in Excel template. Finds the header row by locating
 *  "empCode" (so it tolerates title rows above it) and drops the example row.
 *  SheetJS is a heavy dep, so it's dynamically imported — loaded only when an
 *  admin actually picks a file, keeping it out of the initial bundle. */
async function parseXlsx(data: ArrayBuffer): Promise<ParsedRow[]> {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(data, { type: "array" });
  const ws = wb.Sheets["Employees"] ?? wb.Sheets[wb.SheetNames[0]];
  if (!ws) return [];
  // raw: false → use displayed text, so codes/phones aren't coerced to numbers.
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    blankrows: false,
    defval: "",
    raw: false,
  });
  const headerIdx = aoa.findIndex((row) => row.some((c) => key(c) === "empcode"));
  if (headerIdx < 0) return [];
  const header = aoa[headerIdx].map(key);
  const rows: ParsedRow[] = [];
  for (let i = headerIdx + 1; i < aoa.length; i++) {
    const cols = aoa[i];
    if (!cols.some((c) => cell(c) !== "")) continue; // skip blank rows
    const get = (headerKey: string) => {
      const idx = header.indexOf(headerKey);
      return idx >= 0 ? cell(cols[idx]) : "";
    };
    const row = buildRow(get, i + 1); // 1-based sheet row
    if (isExample(row.empCode)) continue;
    rows.push(row);
  }
  return rows;
}

export interface BulkEmployeeImportDialogProps {
  onImport: (rows: BulkImportEmployeeRow[]) => Promise<BulkImportEmployeesResult>;
  onCompleted?: () => void;
  title?: string;
  description?: string;
  trigger?: React.ReactNode;
}

export function BulkEmployeeImportDialog({
  onImport,
  onCompleted,
  title = "Bulk import employees",
  description = "Upload the filled-in Excel template (or paste CSV). Sites are matched by code, departments by name, managers by empCode.",
  trigger,
}: BulkEmployeeImportDialogProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [fileRows, setFileRows] = useState<ParsedRow[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportEmployeesResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parsed = useMemo(() => (fileRows ? fileRows : parseCsv(text)), [fileRows, text]);
  const validRows = parsed.filter((r) => !r._error);
  const invalidRows = parsed.filter((r) => r._error);

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const rows = await parseXlsx(await file.arrayBuffer());
      if (rows.length === 0) {
        toast.error("No employee rows found", {
          description: "Upload the filled-in Employees sheet from the template.",
        });
        return;
      }
      setFileRows(rows);
      setFileName(file.name);
      setText("");
    } catch (err) {
      toast.error("Could not read file", {
        description: err instanceof Error ? err.message : "Make sure it's a valid .xlsx file.",
      });
    } finally {
      // allow re-selecting the same file after a clear
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearFile = () => {
    setFileRows(null);
    setFileName("");
  };

  const submit = async () => {
    if (validRows.length === 0) {
      toast.error("No valid rows to import");
      return;
    }
    setSubmitting(true);
    try {
      const res = await onImport(
        validRows.map(({ _lineNumber, _error, ...row }) => row),
      );
      setResult(res);
      toast.success(`Imported ${res.created.length} of ${res.totalProcessed} employees`, {
        description: res.skipped.length > 0 ? `${res.skipped.length} skipped — see details` : undefined,
        duration: 8000,
      });
      onCompleted?.();
    } catch (e) {
      toast.error("Import failed", { description: importFailureMessage(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setText("");
    setFileRows(null);
    setFileName("");
    setResult(null);
  };

  const handleOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" variant="outline">
            <Upload className="mr-1.5 h-4 w-4" /> Bulk import
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="grid gap-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={onFileChosen}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> Upload Excel file
              </Button>
              <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
                <a href={TEMPLATE_URL} download>
                  <Download className="mr-1.5 h-3 w-3" /> Download template
                </a>
              </Button>
            </div>

            {fileRows ? (
              <div className="flex items-center justify-between rounded border bg-muted/40 px-3 py-2 text-xs">
                <span className="flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-primary" />
                  <span className="font-medium">{fileName}</span>
                  <span className="text-muted-foreground">— {parsed.length} rows</span>
                </span>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={clearFile}>
                  <X className="mr-1 h-3 w-3" /> Clear
                </Button>
              </div>
            ) : (
              <>
                <Label className="text-xs text-muted-foreground">…or paste CSV rows</Label>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={CSV_PLACEHOLDER}
                  className="min-h-[160px] font-mono text-xs"
                />
              </>
            )}

            {parsed.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="border-success/30 bg-success/10 text-success">
                  {validRows.length} valid
                </Badge>
                {invalidRows.length > 0 && (
                  <Badge variant="outline" className="border-destructive/30 bg-destructive/10 text-destructive">
                    {invalidRows.length} invalid
                  </Badge>
                )}
              </div>
            )}
            {invalidRows.length > 0 && (
              <div className="max-h-32 overflow-auto rounded border bg-muted/50 p-2 text-xs">
                {invalidRows.map((r) => (
                  <div key={r._lineNumber} className="flex items-start gap-2 py-0.5">
                    <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
                    <span className="text-muted-foreground">
                      Row {r._lineNumber}: <span className="text-destructive">{r._error}</span>
                      {r.empCode && <> — {r.empCode}</>}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] text-muted-foreground">
              Required: empCode, name. Optional: email, phone, department, role, primarySiteCode, managerCode, status.
              Status: ACTIVE | INACTIVE (defaults to ACTIVE). managerCode refers to another employee's empCode —
              the manager may already exist or be another row in the same file.
            </div>
          </div>
        )}

        {result && (
          <div className="grid gap-3 py-2">
            <div className="flex items-center gap-2 rounded-lg border bg-success/10 p-3 text-sm">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <span className="font-medium">{result.created.length} employees created</span>
            </div>

            <div className="text-[11px] text-muted-foreground">
              Employee records were created. To let them sign in to the mobile app, invite each one
              separately from the employee list — bulk import does not send logins.
            </div>

            {result.skipped.length > 0 && (
              <div className="max-h-40 overflow-auto rounded border border-destructive/30 bg-destructive/5 p-2 text-xs">
                <div className="mb-1 text-[10px] font-medium uppercase text-destructive">
                  {result.skipped.length} skipped
                </div>
                {result.skipped.map((s, i) => (
                  <div key={i} className="flex justify-between gap-3 py-0.5">
                    <span className="font-mono text-muted-foreground">{s.empCode}</span>
                    <span className="text-right text-destructive">{s.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting || validRows.length === 0}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import {validRows.length > 0 && `(${validRows.length})`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
